import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Athlete = {
  id: string;
  name?: string;
  sports?: string;
  graduationYear?: number;
};

const legacyTheme: CSSProperties = {
  "--background": "220 58% 9%",
  "--foreground": "0 0% 100%",
  "--card": "221 45% 14%",
  "--card-foreground": "0 0% 100%",
  "--muted": "221 35% 18%",
  "--muted-foreground": "215 20% 80%",
  "--border": "40 70% 55%",
  "--input": "221 25% 20%",
  "--ring": "40 70% 55%",
};

// Sorted by sport name, then gender (Men's before Women's); "other" kept last as the catch-all.
// Displayed via titleCase(). Keep this list in sync with AthleteProfile.tsx and mobile/src/app/create-athlete.tsx.
const sportOptions = [
  "baseball",
  "men's basketball",
  "women's basketball",
  "cross country",
  "football",
  "men's golf",
  "women's golf",
  "men's soccer",
  "women's soccer",
  "softball",
  "tennis",
  "track and field",
  "men's volleyball",
  "women's volleyball",
  "other",
];

function titleCase(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function buildDefaultName(user: any): string {
  // Only pre-fill if we have an actual first/last name stored on the account.
  // Never derive a name from the email address — users end up submitting
  // their email prefix (e.g. "cristarobinson2276") as the athlete name.
  const first = typeof user?.firstName === "string" ? user.firstName.trim() : "";
  const last = typeof user?.lastName === "string" ? user.lastName.trim() : "";
  return `${first} ${last}`.trim();
}

function validateAthleteName(name: string): string | null {
  if (!name) return "Please enter your athlete's full name.";
  if (name.includes("@")) return "That looks like an email address. Please enter your athlete's name.";
  if (/\d/.test(name)) return "Athlete name should not contain numbers. Enter a first and last name.";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return "Please enter both a first and last name.";
  if (parts.some((p) => p.length < 2)) return "Each part of the name must be at least 2 characters.";
  return null;
}

async function fetchAthleteProfile(): Promise<Athlete | null> {
  const res = await apiRequest("/athletes/me");
  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload?.message || "Failed to fetch athlete profile");
  }
  return res.json();
}

export default function WelcomeSetup() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const nextPath = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("next") || "/dashboard";
  }, []);

  const currentYear = new Date().getFullYear();
  const gradYearOptions = useMemo(() => {
    const years: number[] = [];
    for (let year = currentYear; year <= currentYear + 10; year += 1) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  const { data: athlete, isLoading: isLoadingAthlete } = useQuery({
    queryKey: ["/api/athletes/me"],
    queryFn: fetchAthleteProfile,
    retry: false,
  });

  const [name, setName] = useState("");
  const [sport, setSport] = useState("softball");
  const [graduationYear, setGraduationYear] = useState<number>(currentYear + 3);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (athlete) {
      setName(athlete.name || "");
      setSport((athlete.sports || "softball").toLowerCase());
      setGraduationYear(athlete.graduationYear || currentYear + 3);
      return;
    }

    setName(buildDefaultName(user));
  }, [athlete, currentYear, user]);

  const hasCompleteProfile = Boolean(
    athlete?.id && athlete?.sports?.trim() && typeof athlete?.graduationYear === "number"
  );

  useEffect(() => {
    if (!isLoadingAthlete && hasCompleteProfile) {
      setLocation(nextPath);
    }
  }, [hasCompleteProfile, isLoadingAthlete, nextPath, setLocation]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrorMessage("");

    const cleanedName = name.trim();
    const cleanedSport = sport.trim().toLowerCase();

    const nameError = validateAthleteName(cleanedName);
    if (nameError) {
      setErrorMessage(nameError);
      return;
    }

    if (!cleanedSport || !graduationYear) {
      setErrorMessage("Please complete all required fields.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: titleCase(cleanedName),
        sports: cleanedSport,
        graduationYear,
      };

      const res = athlete?.id
        ? await apiRequest(`/athletes/${athlete.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
        : await apiRequest("/athletes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || "Could not save your profile setup.");
      }

      await queryClient.invalidateQueries({ queryKey: ["/api/athletes/me"] });
      await queryClient.invalidateQueries({ queryKey: ["/api/athletes"] });
      setLocation(nextPath);
    } catch (error: any) {
      setErrorMessage(error?.message || "Could not save your profile setup.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container mx-auto min-h-screen max-w-2xl px-4 py-8 text-white" style={legacyTheme}>
      <Card className="border-[#E0A458]/45 bg-[#172844] shadow-xl shadow-black/25">
        <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70">
          <CardTitle className="font-montserrat text-2xl text-white">Finish Setup</CardTitle>
          <p className="text-sm text-slate-300">
            We need your athlete sport and graduation year to personalize recruiting calendar dates, deadlines, and guidance.
          </p>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="athlete-name">Athlete Name</Label>
              <Input
                id="athlete-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="First and last name (e.g. Sarah Williams)"
                required
                className="bg-[#0B1220]"
              />
              <p className="text-xs text-slate-400">Enter your athlete's real first and last name.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sport">Primary Sport</Label>
              <select
                id="sport"
                value={sport}
                onChange={(event) => setSport(event.target.value)}
                required
                className="h-10 w-full rounded-md border border-[#415A77] bg-[#0B1220] px-3 text-sm text-white"
              >
                {sportOptions.map((option) => (
                  <option key={option} value={option}>
                    {titleCase(option)}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="graduation-year">Graduation Year</Label>
              <select
                id="graduation-year"
                value={graduationYear}
                onChange={(event) => setGraduationYear(Number(event.target.value))}
                required
                className="h-10 w-full rounded-md border border-[#415A77] bg-[#0B1220] px-3 text-sm text-white"
              >
                {gradYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {errorMessage ? <p className="text-sm text-red-400">{errorMessage}</p> : null}

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-[#E0A458] text-black hover:bg-[#f0b467]"
            >
              {saving ? "Saving..." : "Save and Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
